import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useRenderSnippet, useSnippets } from './queries';

interface SnippetPickerProps {
  ticketId: string | null;
  onInsert: (text: string) => void;
}

/**
 * Auswahl eines Textbausteins -- F1-36, K-18.
 *
 * Die Platzhalter werden **auf dem Server** ersetzt. Das ist kein Umweg: Die
 * Werte stammen aus dem Ticket und -- ab Phase 2 -- aus dem Kunden, und der
 * Browser haette sie sonst alle vorliegen muessen. Die Liste der verfuegbaren
 * Felder bliebe zudem an zwei Stellen zu pflegen.
 *
 * Bleiben Platzhalter offen, werden sie **gemeldet und nicht verschwiegen**.
 * Eine stille Luecke saehe aus wie ein fertiger Text -- und ginge so zum
 * Kunden.
 */
export function SnippetPicker({ ticketId, onInsert }: SnippetPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const snippets = useSnippets(open);
  const render = useRenderSnippet();

  const active = (snippets.data ?? []).filter((snippet) => snippet.is_active);

  return (
    <div className="snippet-picker">
      <Button
        variant="ghost"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        {t('snippets.insert')}
      </Button>

      {open ? (
        <div className="snippet-picker__panel">
          {snippets.isPending ? <p className="table__muted">{t('app.loading')}</p> : null}

          {snippets.isSuccess && active.length === 0 ? (
            <p className="table__muted">{t('snippets.none')}</p>
          ) : null}

          <ul className="snippet-picker__list">
            {active.map((snippet) => (
              <li key={snippet.id}>
                <Button
                  variant="ghost"
                  busy={render.isPending && render.variables.snippetId === snippet.id}
                  onClick={() => {
                    render.mutate(
                      { snippetId: snippet.id, ticketId },
                      {
                        onSuccess: (result) => {
                          onInsert(result.body);
                          setOpen(false);
                        },
                      },
                    );
                  }}
                >
                  <span className="snippet-picker__shortcut">@@{snippet.shortcut}</span>{' '}
                  {snippet.name}
                </Button>
              </li>
            ))}
          </ul>

          {render.isError ? <ErrorNotice error={render.error} /> : null}
        </div>
      ) : null}

      {/* Ausserhalb des Menues, weil es sich beim Einsetzen schliesst. Stuende
          der Hinweis darin, saehe ihn niemand -- und ein Text mit offenem
          Platzhalter ginge unbemerkt an den Kunden. */}
      {render.data && (render.data.unresolved ?? []).length > 0 ? (
        <p className="field__hint" role="status">
          {t('snippets.unresolved', { fields: (render.data.unresolved ?? []).join(', ') })}
        </p>
      ) : null}
    </div>
  );
}
